import React, { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ReactDOM from 'react-dom/client';
import BrowserRoutes from './components/BrowserRoutes';
import * as Sentry from '@sentry/react';
import Hotjar from '@hotjar/browser';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import './scss/theme.scss';
import {
  ConsentProvider,
  CookieBanner,
  CookiePreferences,
} from './scaffold/cookie-consent';
const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

if (import.meta.env.VITE_HOTJAR_ID) {
  Hotjar.init(import.meta.env.VITE_HOTJAR_ID, 6);
}

if (import.meta.env.VITE_SENTRY_DSN) {
  const host = window.location.host;
  let env;
  if (host === 'gannet.ai') {
    env = 'production';
  } else if (host.includes('localhost')) {
    env = 'local';
  } else if (host.includes('dev')) {
    env = 'dev';
  } else {
    env = host;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: env,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ConsentProvider
          gaId={import.meta.env.VITE_GA_MEASUREMENT_ID}
          clarityId={import.meta.env.VITE_HOTJAR_ID}
        >
          <BrowserRoutes />
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="top-right"
          />
          <CookieBanner />
          <CookiePreferences />
        </ConsentProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
);
